/**
 * Configuración SEO del sitio.
 *
 * Esta SPA sirve un único `index.html` estático para toda ruta (rewrite de
 * Vercel `/(.*) → /index.html`), por lo que el `<head>` se gestiona en cliente:
 * el componente `src/components/Seo.tsx` actualiza imperativamente `canonical`,
 * `robots`, `<title>` y `<meta name="description">` al montarse cada página.
 *
 * Aquí viven la URL canónica del sitio, el mapa tipado de metadatos por ruta y
 * la normalización del canonical.
 */

/** Host canónico de producción: apex sin `www`. Base de todas las URLs absolutas. */
export const SITE_URL = 'https://maiabuilder.ai';

/** Directivas de robots soportadas. Unión cerrada para que el tipado las vigile. */
export type RobotsDirective = 'index,follow' | 'noindex,nofollow' | 'noindex,follow';

/** Metadatos SEO de una ruta: título, descripción y directiva de indexación. */
export interface SeoMeta {
  title: string;
  description: string;
  robots: RobotsDirective;
}

/**
 * Mapa tipado de SEO por ruta pública estática. Títulos y descripciones
 * distintos por ruta reducen la señal de "contenido duplicado" de Google Search
 * Console. Las rutas legales (`/privacidad`, `/terminos`) derivan sus metadatos
 * del documento bilingüe en `LegalPage`, y `/blog/:slug` del artículo cargado.
 */
export const SEO_BY_ROUTE = {
  '/': {
    title: 'MaIA — Agentes de IA para tu equipo | Sin código',
    description:
      'Crea tu primer agente de IA en 30 minutos. Automatiza ventas, soporte y operaciones sin código. Agenda un demo.',
    robots: 'index,follow',
  },
  '/blog': {
    title: 'Blog · MaIA — Ideas y guías de agentes de IA',
    description:
      'Tutoriales, casos de uso y novedades de producto para sacar más partido a tus agentes de IA con MaIA.',
    robots: 'index,follow',
  },
  '/turismo': {
    title: 'Turismo & Hotelería · MaIA',
    description:
      'Agentes de IA para turismo y hotelería: automatiza reservas, atención al huésped y operaciones sin código con MaIA.',
    robots: 'index,follow',
  },
} as const satisfies Record<string, SeoMeta>;

/** SEO de las pantallas privadas del panel de administración: fuera del índice. */
export const ADMIN_SEO: SeoMeta = {
  title: 'Administración · MaIA',
  description: 'Panel privado de administración de MaIA.',
  robots: 'noindex,nofollow',
};

/**
 * SEO de la ruta comodín (404). `noindex` para que no se indexe el soft-404 que
 * provoca el rewrite (Vercel responde 200 a cualquier path), pero `follow` para
 * que el crawler siga los enlaces de vuelta al sitio.
 */
export const NOT_FOUND_SEO: SeoMeta = {
  title: 'Página no encontrada · MaIA',
  description: 'La dirección que abriste no existe o cambió de sitio.',
  robots: 'noindex,follow',
};

/** Descripción de reserva para un artículo del blog sin `excerpt`. */
export const BLOG_ARTICLE_FALLBACK_DESCRIPTION =
  'Lee este artículo del blog de MaIA sobre agentes de IA, automatización y producto.';

/**
 * URL canónica absoluta para un `pathname`.
 *
 * Normaliza: ignora query string y hash, y elimina la barra final salvo en la
 * raíz (`/`). Así `/turismo`, `/turismo/` y `/turismo?x=1` producen el mismo
 * canonical `https://maiabuilder.ai/turismo`, y `/` produce
 * `https://maiabuilder.ai/`.
 */
export function canonicalUrl(pathname: string): string {
  let path = pathname.split('?')[0].split('#')[0];
  if (path.length > 1) path = path.replace(/\/+$/, '');
  if (!path) path = '/';
  return `${SITE_URL}${path}`;
}
